import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  downloadAdminAnswerTiming,
  downloadAdminAssessmentData,
  getAdminAssessmentData,
  getEnterprises,
} from '../../api/admin';
import AdminLayout from '../../components/AdminLayout';
import Alert from '../../components/Alert';
import { formatShanghaiDateTime } from '../../utils/dateTime';

const PAGE_SIZE = 25;
const EMPTY_FILTERS = {
  search: '',
  enterprise_id: '',
  status: '',
  assessment_type: '',
  depth: '',
  started_from: '',
  started_to: '',
};

function queryParams(filters, page) {
  const params = { page, page_size: PAGE_SIZE };
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== '') params[key] = value;
  });
  return params;
}

function durationLabel(value) {
  if (value == null) return '—';
  const seconds = Math.max(0, Number(value) || 0);
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.floor(seconds % 60);
  return `${seconds} 秒${minutes ? `（${minutes}分${remainder}秒）` : ''}`;
}

export default function AdminAssessmentDataPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ results: [], summary: {}, total_pages: 1 });
  const [enterprises, setEnterprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [detailExportId, setDetailExportId] = useState(null);

  useEffect(() => {
    getEnterprises()
      .then(({ data: response }) => setEnterprises(response.results ?? response))
      .catch(() => setEnterprises([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    setError('');
    getAdminAssessmentData(queryParams(appliedFilters, page))
      .then(({ data: response }) => setData(response))
      .catch((requestError) => setError(requestError.response?.data?.detail || '测评数据加载失败'))
      .finally(() => setLoading(false));
  }, [appliedFilters, page]);

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters({ ...filters });
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const exportSummary = async () => {
    setExporting(true);
    setError('');
    const params = queryParams(appliedFilters, 1);
    delete params.page;
    delete params.page_size;
    const result = await downloadAdminAssessmentData(params);
    if (!result.ok) setError(result.error);
    setExporting(false);
  };

  const exportDetail = async (assessmentId) => {
    setDetailExportId(assessmentId);
    setError('');
    const result = await downloadAdminAnswerTiming(assessmentId);
    if (!result.ok) setError(result.error);
    setDetailExportId(null);
  };

  const summary = data.summary || {};
  const rows = data.results || [];
  const totalPages = data.total_pages || 1;

  return (
    <AdminLayout title="测评数据">
      {error && <Alert type="error" style={{ marginBottom: 14 }}>{error}</Alert>}

      <div style={summaryGridStyle}>
        <SummaryCard label="筛选结果" value={summary.total ?? 0} unit="份" />
        <SummaryCard label="已完成" value={summary.completed ?? 0} unit="份" color="#16a34a" />
        <SummaryCard label="进行中" value={summary.in_progress ?? 0} unit="份" color="#2563eb" />
        <SummaryCard label="已有时长埋点" value={summary.with_timing ?? 0} unit="份" color="#7c3aed" />
      </div>

      <div style={filterCardStyle}>
        <div style={filterGridStyle}>
          <input
            value={filters.search}
            onChange={(event) => setFilters((old) => ({ ...old, search: event.target.value }))}
            onKeyDown={(event) => event.key === 'Enter' && applyFilters()}
            placeholder="姓名 / 邮箱 / 企业"
            style={controlStyle}
          />
          <select value={filters.enterprise_id} onChange={(event) => setFilters((old) => ({ ...old, enterprise_id: event.target.value }))} style={controlStyle}>
            <option value="">全部企业</option>
            {enterprises.map((enterprise) => <option key={enterprise.id} value={enterprise.id}>{enterprise.name}</option>)}
          </select>
          <select value={filters.status} onChange={(event) => setFilters((old) => ({ ...old, status: event.target.value }))} style={controlStyle}>
            <option value="">全部状态</option>
            <option value="in_progress">进行中</option>
            <option value="completed">已完成</option>
            <option value="abandoned">已放弃</option>
          </select>
          <select value={filters.assessment_type} onChange={(event) => setFilters((old) => ({ ...old, assessment_type: event.target.value }))} style={controlStyle}>
            <option value="">全部阶段/路线</option>
            <option value="baseline">初测</option>
            <option value="new_question_retest">新题路线</option>
            <option value="same_question_retest">快照路线</option>
          </select>
          <select value={filters.depth} onChange={(event) => setFilters((old) => ({ ...old, depth: event.target.value }))} style={controlStyle}>
            <option value="">全部题量</option>
            <option value="quick">快速（30题）</option>
            <option value="standard">标准（60题）</option>
            <option value="deep">深度（90题）</option>
          </select>
          <label style={dateControlStyle}>开始日期从<input type="date" value={filters.started_from} onChange={(event) => setFilters((old) => ({ ...old, started_from: event.target.value }))} style={dateInputStyle} /></label>
          <label style={dateControlStyle}>开始日期至<input type="date" value={filters.started_to} onChange={(event) => setFilters((old) => ({ ...old, started_to: event.target.value }))} style={dateInputStyle} /></label>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 14, flexWrap: 'wrap' }}>
          <button type="button" onClick={resetFilters} style={secondaryButtonStyle}>重置</button>
          <button type="button" onClick={applyFilters} style={primaryButtonStyle}>查询</button>
          <button type="button" onClick={exportSummary} disabled={exporting} style={exportButtonStyle}>
            {exporting ? '正在导出…' : '导出当前筛选 CSV'}
          </button>
        </div>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 1300, borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              {['测评', '用户 / 企业', '阶段 / 后台路线', '进度', '开始时间', '完成时间', '测评总跨度', '逐题累计用时', '时长覆盖', '操作'].map((heading) => (
                <th key={heading} style={thStyle}>{heading}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} style={emptyStyle}>加载中...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={10} style={emptyStyle}>当前筛选条件下暂无测评数据</td></tr>
            ) : rows.map((row) => (
              <tr key={row.assessment_id} style={{ borderTop: '1px solid #f1f5f9' }}>
                <td style={tdStyle}>
                  <div style={{ fontWeight: 700, color: '#1a3a6b' }}>#{row.assessment_id}</div>
                  <div style={mutedStyle}>{row.depth_label}</div>
                </td>
                <td style={tdStyle}>
                  <div style={{ fontWeight: 600 }}>{row.user_display_name || '未命名用户'}</div>
                  <div style={mutedStyle}>{row.user_email || '无邮箱'}</div>
                  <div style={mutedStyle}>{row.enterprise_name || '个人用户'}</div>
                </td>
                <td style={tdStyle}>
                  <div style={{ fontWeight: 700, color: row.assessment_type === 'baseline' ? '#475569' : '#7c3aed' }}>{row.route_label}</div>
                  <div style={mutedStyle}>初测 #{row.baseline_assessment_id}</div>
                </td>
                <td style={tdStyle}>
                  <div style={{ fontWeight: 700 }}>{row.answered_count}/{row.total_questions}（{row.completion_percent}%）</div>
                  <div style={{ ...mutedStyle, color: row.status === 'completed' ? '#16a34a' : '#64748b' }}>{row.status_label}</div>
                </td>
                <td style={tdStyle}>{formatShanghaiDateTime(row.started_at)}</td>
                <td style={tdStyle}>{formatShanghaiDateTime(row.completed_at)}</td>
                <td style={tdStyle}>{durationLabel(row.elapsed_seconds)}</td>
                <td style={tdStyle}>{row.answer_time_total_seconds} 秒</td>
                <td style={tdStyle}>{row.timing_captured_count}/{row.answered_count}（{row.timing_coverage_percent}%）</td>
                <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                  <button type="button" onClick={() => navigate(`/admin/reports/${row.assessment_id}`)} style={linkButtonStyle}>查看详情</button>
                  <button type="button" onClick={() => exportDetail(row.assessment_id)} disabled={detailExportId === row.assessment_id} style={smallExportButtonStyle}>
                    {detailExportId === row.assessment_id ? '导出中…' : '逐题 CSV'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginTop: 16 }}>
        <button type="button" disabled={page <= 1} onClick={() => setPage((old) => Math.max(1, old - 1))} style={secondaryButtonStyle}>上一页</button>
        <span style={{ fontSize: 13, color: '#64748b' }}>第 {page} / {totalPages} 页</span>
        <button type="button" disabled={page >= totalPages} onClick={() => setPage((old) => Math.min(totalPages, old + 1))} style={secondaryButtonStyle}>下一页</button>
      </div>
    </AdminLayout>
  );
}

function SummaryCard({ label, value, unit, color = '#1a3a6b' }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 18px' }}>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 5 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color }}>{value}<span style={{ fontSize: 12, marginLeft: 4, fontWeight: 500 }}>{unit}</span></div>
    </div>
  );
}

const summaryGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 };
const filterCardStyle = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 16 };
const filterGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 };
const controlStyle = { minHeight: 40, padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 12, background: '#fff' };
const dateControlStyle = { ...controlStyle, display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', whiteSpace: 'nowrap' };
const dateInputStyle = { border: 0, minWidth: 0, width: '100%', color: '#334155', background: 'transparent' };
const primaryButtonStyle = { minHeight: 40, padding: '8px 18px', border: 0, borderRadius: 8, background: '#1a3a6b', color: '#fff', fontWeight: 700, cursor: 'pointer' };
const secondaryButtonStyle = { minHeight: 40, padding: '8px 16px', border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff', color: '#475569', cursor: 'pointer' };
const exportButtonStyle = { ...primaryButtonStyle, background: '#7c3aed' };
const thStyle = { padding: '11px 12px', textAlign: 'left', color: '#64748b', fontWeight: 700, whiteSpace: 'nowrap' };
const tdStyle = { padding: '12px', color: '#334155', verticalAlign: 'top' };
const mutedStyle = { color: '#94a3b8', fontSize: 11, marginTop: 3 };
const emptyStyle = { padding: 40, textAlign: 'center', color: '#94a3b8' };
const linkButtonStyle = { border: 0, background: 'transparent', color: '#2563eb', fontWeight: 700, cursor: 'pointer', padding: '5px 7px' };
const smallExportButtonStyle = { ...linkButtonStyle, color: '#7c3aed' };
